import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export type Paper = 'a4' | 'a4-landscape' | '80mm';

const PAGE_CSS: Record<Paper, string> = {
  a4: `@page { size: A4; margin: 12mm 12mm 16mm;
  @bottom-right { content: "Page " counter(page) " / " counter(pages); font: 8pt "Inter Variable", system-ui, sans-serif; color: #667085; } }`,
  'a4-landscape': `@page { size: A4 landscape; margin: 10mm 12mm 14mm;
  @bottom-right { content: "Page " counter(page) " / " counter(pages); font: 8pt "Inter Variable", system-ui, sans-serif; color: #667085; } }`,
  '80mm': '@page { size: 80mm auto; margin: 0; }',
};

const BASE_CSS = `
@media screen { .sm-print-root { display: none; } }
@media print {
  /* Light paper even in dark mode: the dark scheme would paint the page margins dark. */
  :root { color-scheme: light !important; }
  html, body { background: #fff !important; }
  body > *:not(.sm-print-root) { display: none !important; }
  .sm-print-root { display: block; color: #101828; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}`;

/** The print-only surface: everything else on the page is hidden while printing. */
export function PrintSheet({ paper, children }: { paper: Paper; children: ReactNode }) {
  return (
    <div className="sm-print-root" data-paper={paper}>
      <style>{BASE_CSS + PAGE_CSS[paper]}</style>
      {children}
    </div>
  );
}

/**
 * Prints a document through the browser's print dialog (thermal 80 mm or A4). Render `element`
 * somewhere in the screen; call `print(paper, node)` to print.
 */
export function usePrint() {
  const [job, setJob] = useState<{ paper: Paper; node: ReactNode } | null>(null);

  useEffect(() => {
    if (!job) return;
    const done = () => setJob(null);
    window.addEventListener('afterprint', done);
    // Let the portal paint (and fonts settle) before opening the dialog.
    const t = window.setTimeout(() => window.print(), 80);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('afterprint', done);
    };
  }, [job]);

  const print = useCallback((paper: Paper, node: ReactNode) => setJob({ paper, node }), []);
  const element = job
    ? createPortal(<PrintSheet paper={job.paper}>{job.node}</PrintSheet>, document.body)
    : null;
  return { print, element, printing: job !== null };
}
